import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError, AxiosResponse } from 'axios'
import { fetchDataFromApi } from '../services/fetchDataFromApi.tsx'
import { NwLecturerApplicationDto } from '../types/NwLecturerApplicationDto.tsx'

interface SettingState {
  selectedDocent: NwLecturerApplicationDto | null
  docentList: NwLecturerApplicationDto[] | undefined
  docentRequestedList: NwLecturerApplicationDto[] | undefined
  loading: 'idle' | 'pending' | 'succeeded' | 'failed'
  error: string | null
  statusCode: number | null
}

interface FetchApiDataOptions {
  url: string
  token: string
  method?: string
}

const initialState: SettingState = {
  selectedDocent: null,
  docentList: undefined,
  docentRequestedList: undefined,
  loading: 'idle',
  error: null,
  statusCode: null,
}

// Alle Dozenten
export const fetchAllDocentFromApi = createAsyncThunk('api/fetchAllDocentFromApi', async ({ url, token }: FetchApiDataOptions) => {
  try {
    const data: AxiosResponse | AxiosError = await fetchDataFromApi(url, token)
    return data
  } catch (error: any) {
    // Fehlerbehandlung
  }
})

// All Dozenten mit Anfrage
export const fetchRequestedDocentFromApi = createAsyncThunk('api/fetchRequesteDocentFromApi', async ({ url, token }: FetchApiDataOptions) => {
  try {
    const data: AxiosResponse | AxiosError = await fetchDataFromApi(url, token)
    return data
  } catch (error: any) {
    // Fehlerbehandlung
  }
})

const docentSlice = createSlice({
  name: 'docentStore',
  initialState,
  reducers: {
    setSelectedDocent: (state, action) => {
      state.selectedDocent = action.payload
    },
  },
  extraReducers: (builder) => {
    builder

      // Alle Dozenten
      .addCase(fetchAllDocentFromApi.pending, (state) => {
        state.loading = 'pending'
      })
      .addCase(fetchAllDocentFromApi.fulfilled, (state, action) => {
        const result = action.payload
        if (result instanceof AxiosError) {
          if (result?.status < 200 || result.status >= 300) {
            state.statusCode = result.status
          } else if (result.code === 'ERR_NETWORK') {
            state.statusCode = 503
          } else if (result.code === 'ERR_BAD_REQUEST') {
            state.statusCode = 403
          } else {
            state.statusCode = result.status
          }
          state.loading = 'failed'
          state.error = result.statusText
        } else if (result) {
          state.loading = 'succeeded'
          state.docentList = action.payload?.data
          state.statusCode = result.status
        }
      })
      .addCase(fetchAllDocentFromApi.rejected, (state, action) => {
        state.loading = 'failed'
        state.error = action.error.message || 'An error occurred'
      })

      // Alle Anfragen Dozenten
      .addCase(fetchRequestedDocentFromApi.pending, (state) => {
        state.loading = 'pending'
      })
      .addCase(fetchRequestedDocentFromApi.fulfilled, (state, action) => {
        const result = action.payload
        if (result instanceof AxiosError) {
          if (result?.status < 200 || result.status >= 300) {
            state.statusCode = result.status
          } else if (result.code === 'ERR_NETWORK') {
            state.statusCode = 503
          } else if (result.code === 'ERR_BAD_REQUEST') {
            state.statusCode = 403
          } else {
            state.statusCode = result.status
          }
          state.loading = 'failed'
          state.error = result.statusText
        } else if (result) {
          state.loading = 'succeeded'
          state.docentRequestedList = action.payload?.data
          state.statusCode = result.status
        }
      })
      .addCase(fetchRequestedDocentFromApi.rejected, (state, action) => {
        state.loading = 'failed'
        state.error = action.error.message || 'An error occurred'
      })
  },
})
export const { setSelectedDocent } = docentSlice.actions
export default docentSlice.reducer
